// Column types and their rendering. Shared by ReportView (screen) and the CSV/XLSX exports, so
// what is on screen and what lands in the file cannot drift.
//
// TYPES
//   'text'   plain string
//   'money'  dollars, TWO decimals      — $1,234.56
//   'value'  dollars, WHOLE             — $1,234,567   (assessed values; cents are noise at this scale)
//   'int'    counted things             — 1,234
//   'status' a status label (text, but reserved so a report can restyle statuses without a fork)
//   'year'    a 4-digit year, never comma-grouped
//   'rate'    a TAX RATE in percent units as a plain number (1.178467), six decimals, no % sign
//   'percent' a PERCENTAGE CHANGE in percent units (41.18), two decimals, with a % sign
//   'date'    a date — LOCAL-time parsing only
//
// ⛔ ESTIMATE FLAG. Any display value may be `{ value, estimated }` instead of a bare primitive.
// The renderer appends an asterisk and the footer shows "* denotes an estimate" when any row
// carries one; the XLSX export keeps the cell numeric and puts the asterisk in the number format;
// the CSV export either appends it or moves it to a companion column. Nothing sets the flag yet —
// the plumbing is here for the pending business decision on estimated tax figures.

// ⛔ 'link' is a SCREEN-ONLY presentation: ReportView renders an <a>, and both exporters fall
// through to plain text because neither recognises the type — which is exactly the behaviour
// wanted (clickable in the app, the full URL as text in the file). formatCell also falls through
// to its default branch, so the totals row and any non-ReportView reader get the raw string.
export const COLUMN_TYPES = ['text', 'money', 'value', 'int', 'status', 'year', 'rate', 'percent', 'date', 'link'];

// ⛔ BOTH percent-ish types hold PERCENT UNITS as a plain number — 1.178467 means 1.178467%, and
// 41.18 means 41.18%. They differ only in precision and whether a % sign is shown: a tax rate is
// six decimals and bare (matching the analyst's workbook), a change is two decimals and signed
// with %. Keeping ONE unit convention across both is deliberate; two similar types disagreeing
// about whether the value is 41.18 or 0.4118 is a 100x error waiting for whoever reads them next.

// ⛔ ROUND AT OUTPUT, NEVER IN THE DERIVATION. IEEE doubles make 719254.04 come out of a sum as
// 719254.0399999999. An XLSX number format HIDES that; CSV has no formats, so the raw figure ships
// to the client. Both exports and the screen round here, at the boundary, so the four surfaces
// agree to the cent while the arithmetic upstream keeps full precision.
// ⛔ SUM THEN ROUND, not round then sum: rounding each row first accumulates up to half a cent of
// error per row, which on a few hundred rows is a visible discrepancy against the bill. The totals
// helpers sum raw and round once, here.
export const MONEY_DP = 2;
export const PERCENT_DP = 6;
export const roundTo = (n, dp) => (n == null ? null : Math.round((n + Number.EPSILON) * 10 ** dp) / 10 ** dp);
export function roundForOutput(n, type) {
  if (n == null) return null;
  if (type === 'money' || type === 'value') return roundTo(n, MONEY_DP);
  if (type === 'rate') return roundTo(n, PERCENT_DP);
  if (type === 'percent') return roundTo(n, 2);
  if (type === 'int' || type === 'year') return Math.round(n);
  return n;
}
export const NUMERIC_TYPES = new Set(['money', 'value', 'int', 'rate', 'percent']);   // 'year' is a number but is never summed

// Philip, 2026-10-08: the mark is labelled by what it means — the year has no bill, so the latest prior year's
// bill (its rate) was used.
// Step 8: "Pending savings — Projected" on. Row-level: the row's Status still says it is undecided.
export const PROJECTED_FOOTNOTE = 'Savings on rows still under review or on appeal are projected — read as if the latest level with values were final. They can change.';
export const ESTIMATE_FOOTNOTE = '* Estimated (prior-year rate): this year has no bill yet, so the latest prior year\u2019s bill was used';

// Reduction and Savings while the year's outcome is not known (Appeal Open / Under Review / Value Pending). A
// word, never a blank (no data) and never 0 (we saved nothing). It is text in a numeric column, so every reader
// — screen, XLSX, CSV, totals — already treats it as "not a figure" and leaves it out of sums.
export const PENDING = 'Pending';

// ═══ n/m — A PERCENTAGE THAT WOULD MISLEAD (Philip, 2026-10-10) ═══
// Orlando Airport 2024 vs 2023 read Δ Assessed % 800,708.67%: 2023 was a leasehold assessed at a nominal $100 a
// parcel ($300 in all, $5.43 tax). The dollars were right; the percentage was arithmetic on a placeholder. A
// percent change is shown as 'n/m' when the prior figure is under its floor, or the change is beyond ±NM_MAX_PCT.
// Like PENDING it is text in a numeric column, so every reader leaves it out of sums and sorts it with the blanks.
// The cell is { value: NOT_MEANINGFUL, note } — the note is the hover on screen and the cell comment in the XLSX.
export const NOT_MEANINGFUL = 'n/m';
export const NM_MIN_ASSESSED = 10000;
export const NM_MIN_TAX = 100;
export const NM_MAX_PCT = 500;
export const NOT_MEANINGFUL_FOOTNOTE =
  `n/m = not meaningful: the prior-year figure is too small to measure a percentage against (assessed under `
  + `$${NM_MIN_ASSESSED.toLocaleString('en-US')}, tax under $${NM_MIN_TAX}), or the change is beyond \u00b1${NM_MAX_PCT}%. `
  + 'The \u0394 $ column beside it shows the change in dollars.';
// The explanation a cell carries for its hover, or null.
export const cellNote = (raw) => (raw && typeof raw === 'object' && !(raw instanceof Date) && 'value' in raw && raw.note) || null;
// ⛔ A SECOND, DISTINCT MARK. An estimate and a recovered rate are different claims and must never
// share a symbol: `*` says the rate came from another YEAR, `†` says the rate came from the bill's
// own ARITHMETIC because the printed one was unreadable. A figure can be both, and shows `*†`.
export const RECOVERED_FOOTNOTE =
  '† rate recovered from the bill\u2019s own arithmetic (line amount ÷ taxable value) because the printed rate was unreadable';
// ⛔ A THIRD MARK, on a RATE only (Philip 2026-10-27): the bill prints no rate, so the rate shown is its effective rate —
// the bill's ad valorem ÷ its taxable value. A real figure from this year's bill, but not one the bill states.
export const EFFECTIVE_FOOTNOTE =
  '‡ effective rate: the bill prints no rate, so this is the bill\u2019s ad valorem tax ÷ its taxable value';

// ⛔ NOT A SYMBOL. The estimate and recovered marks are footnote symbols on a FIGURE; this is a claim
// about a whole ROW — that we were not engaged that year — and it is the one statement on the report a
// client could most damagingly misread. It gets the word, in a pill on screen and a Note cell in
// the export, not a character someone has to look up.
// ⛔ "FIGURES", NOT "YEARS" — and the change is load-bearing, not a polish. Baseline is a property of
// an ACCOUNT-YEAR, not of a year: a client whose parcels were engaged in different years has years
// that are baseline for some accounts and represented for others. The earlier wording ("no appeal
// work was performed in those years") is then false on its own detail sheet, where a marked 2025 row
// and an unmarked 2025 row sit together — and on the Summary tab it would sit beside a real savings
// figure while denying any savings are claimed. Stating it at the grain the flag actually has makes
// it true on every sheet and in every mix.
export const BASELINE_FOOTNOTE =
  'Baseline figures (marked) are the client\u2019s own, from before NTRG was engaged, loaded for '
  + 'comparison. No appeal work was performed on them and no savings are claimed against them.';

// The same statement when the Note column (the marker) has been hidden from the export: the file still says
// baseline figures are in it, and how to see which, rather than pointing at a mark that is not there.
export const BASELINE_UNMARKED_FOOTNOTE =
  'Some figures here are baseline: the client\u2019s own, from before NTRG was engaged, loaded for comparison. '
  + 'No appeal work was performed on them and no savings are claimed against them. The Note column, '
  + 'hidden from this export, marks which they are.';

// The Summary tab only — it is the one sheet that states a whole year in a single row, so it is the
// one place a year can be part baseline and part ours with nothing on the row to say so. Emitted
// only when such a year exists; see the condition in propertyExecutiveSummary's exportSheets.
export const BASELINE_MIXED_FOOTNOTE =
  'A year marked \u201cIncludes baseline years\u201d covers both: accounts we did not yet represent that '
  + 'year and accounts we did. The savings shown for it are from the represented accounts only.';

// { value, estimated, recovered, effective } -> parts; anything else passes through unflagged.
export function unwrapValue(raw) {
  if (raw && typeof raw === 'object' && !(raw instanceof Date) && 'value' in raw) {
    return { value: raw.value, estimated: !!raw.estimated, recovered: !!raw.recovered, effective: !!raw.effective };
  }
  return { value: raw, estimated: false, recovered: false, effective: false };
}

export const rawValue = (v) => unwrapValue(v).value;
export const isEstimated = (v) => unwrapValue(v).estimated;
export const isRecovered = (v) => unwrapValue(v).recovered;
export const isEffective = (v) => unwrapValue(v).effective;

// The trailing mark(s) for a display value: '' | '*' | '†' | '‡' and their combinations. One home, so the screen and
// both exports cannot drift apart on what a figure is claiming.
export function valueMarks(raw) {
  const { estimated, recovered, effective } = unwrapValue(raw);
  return `${estimated ? '*' : ''}${recovered ? '†' : ''}${effective ? '‡' : ''}`;
}

// '$1,234.56' | 1234.56 | null -> number | null. Never returns 0 for an unparseable input — a
// silent 0 in a money column is the failure this whole layer exists to avoid.
// ⛔ FULL-STRING MATCH, not parseFloat's prefix scan: parseFloat('3-year average') is 3, and a
// label quietly becoming a figure is the failure this whole layer exists to avoid.
const NUMERIC_STRING = /^-?(\d+(\.\d*)?|\.\d+)$/;
export function toNumber(v) {
  const { value } = unwrapValue(v);
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const s = String(value ?? '').replace(/[$%\s,]/g, '');
  if (!s || !NUMERIC_STRING.test(s)) return null;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

// ⛔ LOCAL-time only. new Date('2026-03-15') parses as UTC midnight and renders a day early west
// of Greenwich — the bug this codebase has been bitten by repeatedly.
export function toLocalDate(v) {
  const { value } = unwrapValue(v);
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? ''));
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
}

// Render one cell for the SCREEN. Blank stays blank — a missing figure must never read as $0.
export function formatCell(raw, type) {
  const { value } = unwrapValue(raw);
  const star = valueMarks(raw);
  if (value == null || value === '') return type === 'text' || type === 'status' ? '' : '—';

  switch (type) {
    case 'money': {
      const n = toNumber(value);
      return n == null ? String(value) : `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${star}`;
    }
    case 'value': {
      const n = toNumber(value);
      return n == null ? String(value) : `$${Math.round(n).toLocaleString()}${star}`;
    }
    case 'int': {
      const n = toNumber(value);
      return n == null ? String(value) : `${Math.round(n).toLocaleString()}${star}`;
    }
    case 'rate': {
      const n = toNumber(value);
      // ⛔ PERCENT UNITS AS A PLAIN NUMBER — 1.178467, no % sign. Matches the analyst's workbook,
      // which stores EffectiveTaxRate as a float with format #,##0.000000 and keeps a SEPARATE
      // column holding =+K8/100 to multiply by. She does the /100 in a visible cell; our file has
      // to sit beside hers on the same desk, so it reads the same way.
      return n == null ? String(value) : `${n.toFixed(PERCENT_DP)}${star}`;
    }
    case 'percent': {
      const n = toNumber(value);
      // A change of nought is a real answer and prints as 0.00%; only ABSENT prints as a dash,
      // which the null guard above already handled.
      return n == null ? String(value) : `${n.toFixed(2)}%${star}`;
    }
    case 'year':
      return `${String(value)}${star}`;
    case 'date': {
      const d = toLocalDate(value);
      return d ? `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}${star}` : String(value);
    }
    default:
      return `${String(value)}${star}`;
  }
}

// Right-align numbers unless the definition says otherwise.
export const defaultAlign = (type) => (NUMERIC_TYPES.has(type) || type === 'year' ? 'right' : 'left');

// Sum a `total: true` column across rows. Returns null when NO row had a number, so an all-blank
// column footers as blank rather than as $0. An estimated contributor makes the total estimated,
// and a recovered one makes it recovered — a total is only as well-sourced as its weakest input.
// ⛔ PENDING rows are left out of the sum (it is the DECIDED total — decidedNote says how many are missing), and
// when EVERY row with a figure-or-pending is pending the total is PENDING itself: never blank, never $0.
export function sumColumn(rows, key) {
  let total = null;
  let estimated = false;
  let recovered = false;
  let pending = false;
  (rows || []).forEach((r) => {
    if (rawValue(r?.[key]) === PENDING) { pending = true; return; }
    const n = toNumber(r?.[key]);
    if (n == null) return;
    total = (total ?? 0) + n;
    if (isEstimated(r?.[key])) estimated = true;
    if (isRecovered(r?.[key])) recovered = true;
  });
  if (total == null) return pending ? PENDING : null;
  if (!estimated && !recovered) return total;
  return { value: total, ...(estimated ? { estimated: true } : {}), ...(recovered ? { recovered: true } : {}) };
}

// "3 of 5 accounts decided, 2 pending" — for a total that leaves pending rows out. Counted over our own rows
// (baseline rows are the client's figures, never pending). Null when nothing is pending, and when EVERYTHING is
// (the total then reads "Pending" by itself). Several years in one set count account-years.
export function decidedNote(rows, key = 'savings') {
  const ours = (rows || []).filter((r) => r && !r.baseline);
  const pending = ours.filter((r) => rawValue(r[key]) === PENDING).length;
  if (!pending || pending === ours.length) return null;
  const unit = new Set(ours.map((r) => String(r.tax_year))).size > 1 ? 'account-years' : 'accounts';
  return `${ours.length - pending} of ${ours.length} ${unit} decided, ${pending} pending`;
}

// The totals row's label: TOTAL, plus the decided note when the total leaves pending rows out.
export const totalLabel = (totals, word = 'TOTAL') => (totals?.__note ? `${word} — ${totals.__note}` : word);

// Does any cell in this data set carry an estimate flag? Drives the footnote.
export function anyEstimated(rows, columns) {
  return (rows || []).some((r) => (columns || []).some((c) => isEstimated(r?.[c.key])));
}

export function anyNotMeaningful(rows, columns) {
  return (rows || []).some((r) => (columns || []).some((c) => rawValue(r?.[c.key]) === NOT_MEANINGFUL));
}

export function anyRecovered(rows, columns) {
  return (rows || []).some((r) => (columns || []).some((c) => isRecovered(r?.[c.key])));
}

export function anyEffective(rows, columns) {
  return (rows || []).some((r) => (columns || []).some((c) => isEffective(r?.[c.key])));
}

// Comparator for a column type — used by defaultSort and header clicks.
export function compareBy(key, type, dir = 'asc') {
  const sign = dir === 'desc' ? -1 : 1;
  return (a, b) => {
    if (NUMERIC_TYPES.has(type) || type === 'year') {
      const x = toNumber(a?.[key]); const y = toNumber(b?.[key]);
      if (x == null && y == null) return 0;
      if (x == null) return 1;          // blanks last, both directions
      if (y == null) return -1;
      return (x - y) * sign;
    }
    if (type === 'date') {
      const x = toLocalDate(a?.[key]); const y = toLocalDate(b?.[key]);
      if (!x && !y) return 0;
      if (!x) return 1;
      if (!y) return -1;
      return (x - y) * sign;
    }
    const x = String(rawValue(a?.[key]) ?? '');
    const y = String(rawValue(b?.[key]) ?? '');
    if (!x && !y) return 0;
    if (!x) return 1;
    if (!y) return -1;
    return x.localeCompare(y) * sign;
  };
}
